import { useEffect, useState } from "react";
import { BadgeCheck, Loader2, Check, X, Eye, FileText, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API_BASE } from "@/lib/api";
import ImageLightbox from "@/components/ImageLightbox";

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700",
  verified: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700",
};
const DOC_LABEL = { cni: "CNI", passeport: "Passeport", permis: "Permis" };

export default function AdminVerifications() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/admin/verifications", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    const t = setTimeout(() => refresh(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const decide = async (userId, decision) => {
    let reason = null;
    if (decision === "reject") {
      reason = window.prompt("Motif du refus (visible par l'utilisateur) :", "Pièce non conforme");
      if (reason === null) return;
    }
    setBusy(userId);
    try {
      await api.post(`/admin/verifications/${userId}/decision`, { decision, reason });
      toast.success(decision === "accept" ? "Compte vérifié ✓" : "Demande refusée");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setBusy(null);
  };

  const docUrl = (userId) => `${API_BASE}/admin/verifications/${userId}/doc?auth=${encodeURIComponent(localStorage.getItem("sl_token") || "")}`;

  return (
    <div data-testid="admin-verifications-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-10 h-10 rounded-xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><BadgeCheck size={18} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold">Vérifications de compte</h1>
          <p className="text-sm text-gray-500">Validez les pièces d'identité pour attribuer le badge « Client vérifié ». Base consultable à tout moment — anti-abus missions quotidiennes.</p>
        </div>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par nom, email ou n° de pièce…"
          className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sl-primary)]/30 focus:border-[var(--sl-primary)]"
          data-testid="verif-search-input"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {["pending", "verified", "rejected", ""].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`vfilter-${s || "all"}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition ${filter === s ? "bg-[var(--sl-primary)] text-white" : "bg-white border text-gray-600 hover:bg-gray-50"}`}>
            {s === "pending" ? "En attente" : s === "verified" ? "Vérifiés" : s === "rejected" ? "Refusés" : "Tous"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-[var(--sl-primary)]" /></div>
      ) : rows.length === 0 ? (
        <div className="sl-card text-center text-gray-400 py-10">Aucune demande.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.user_id} className="rounded-2xl border bg-white p-4 flex flex-wrap items-center gap-3" data-testid={`verif-${r.user_id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold">{r.name || r.email}</div>
                <div className="text-xs text-gray-400">{r.email}</div>
                <div className="text-sm text-gray-600 mt-1">
                  {DOC_LABEL[r.doc_type] || r.doc_type} · {r.full_name} · N° {r.doc_number}
                  {r.doc_number_duplicate && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full" data-testid={`verif-duplicate-${r.user_id}`}>
                      <AlertTriangle size={10} /> Doublon de pièce
                    </span>
                  )}
                </div>
                {r.status !== "pending" && r.decided_at && (
                  <div className="text-xs text-gray-400 mt-1">
                    {r.status === "verified" ? "Validé" : "Décidé"} le {new Date(r.decided_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })} par {r.decided_by || "—"}
                  </div>
                )}
                {r.reason && <div className="text-xs text-rose-600 mt-1">Motif : {r.reason}</div>}
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_BADGE[r.status] || "bg-gray-100"}`}>{r.status}</span>
              {r.has_doc && (
                <button
                  type="button"
                  onClick={() => setLightbox({ src: docUrl(r.user_id), alt: `Pièce — ${r.full_name || r.email}`, isPdf: !!r.doc_is_pdf })}
                  className="sl-btn-outline text-xs"
                  data-testid={`verif-view-${r.user_id}`}
                >
                  <Eye size={13} /> Pièce
                </button>
              )}
              {r.status === "pending" && (
                <div className="flex gap-2">
                  <button onClick={() => decide(r.user_id, "accept")} disabled={busy === r.user_id}
                    className="text-xs inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-60" data-testid={`verif-accept-${r.user_id}`}>
                    {busy === r.user_id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Valider
                  </button>
                  <button onClick={() => decide(r.user_id, "reject")} disabled={busy === r.user_id}
                    className="text-xs inline-flex items-center gap-1 rounded-full border border-rose-200 text-rose-600 px-3 py-1.5 hover:bg-rose-50 disabled:opacity-60" data-testid={`verif-reject-${r.user_id}`}>
                    <X size={12} /> Refuser
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.alt}
          isPdf={lightbox.isPdf}
          downloadHref={lightbox.src}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
